import type { QueryClient } from '@tanstack/react-query';
import type { CreativeEntry, PackEntry } from 'types/shared';
import type { CreativesByHypothesis } from 'features/creatives/api';
import creativesQueryKeys from 'features/creatives/api/queryKeys';
import type { AdSettingTextEntry } from '../api';
import { isDloAdType } from './dloCreativeSelection';

export const NO_TEXTS_AVAILABLE_MESSAGE =
  'No texts available for this flow. Add localized text to continue';

export const normalizeLocale = (locale: string): string => locale.trim().toUpperCase();

export const toTextId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    return Number(value);
  }

  return null;
};

export const getTextAvailableLocales = (text: AdSettingTextEntry): Set<string> => {
  const locales = new Set<string>();

  if (typeof text.language === 'string' && text.language.trim()) {
    locales.add(normalizeLocale(text.language));
  }

  (text.locales ?? []).forEach((locale) => {
    if (locale?.trim()) {
      locales.add(normalizeLocale(locale));
    }
  });

  (text.localized_languages ?? []).forEach((locale) => {
    if (locale?.trim()) {
      locales.add(normalizeLocale(locale));
    }
  });

  (text.localizations ?? []).forEach((localization) => {
    if (localization.language?.trim()) {
      locales.add(normalizeLocale(localization.language));
    }
  });

  (text.available_localizations ?? []).forEach((localization) => {
    if (localization.language?.trim()) {
      locales.add(normalizeLocale(localization.language));
    }
  });

  return locales;
};

const collectCachedCreatives = (
  allCreativesData: readonly unknown[],
  allCreativesWithHypothesisData: readonly unknown[],
): Map<string, CreativeEntry> => {
  const byId = new Map<string, CreativeEntry>();

  allCreativesData.forEach((cacheItem) => {
    if (!Array.isArray(cacheItem)) {
      return;
    }

    cacheItem.forEach((creative) => {
      const typedCreative = creative as CreativeEntry;
      if (typedCreative?.id) {
        byId.set(typedCreative.id, typedCreative);
      }
    });
  });

  allCreativesWithHypothesisData.forEach((cacheItem) => {
    if (!cacheItem || typeof cacheItem !== 'object') {
      return;
    }

    Object.values(cacheItem as CreativesByHypothesis).forEach((creatives) => {
      creatives.forEach((creative) => {
        if (creative?.id) {
          byId.set(creative.id, creative);
        }
      });
    });
  });

  return byId;
};

export const getCreativesByIdFromCache = (
  queryClient: QueryClient,
): Map<string, CreativeEntry> => {
  const allCreativesData = queryClient
    .getQueryCache()
    .findAll({ queryKey: creativesQueryKeys.allCreatives })
    .map((query) => query.state.data);

  const allCreativesWithHypothesisData = queryClient
    .getQueryCache()
    .findAll({ queryKey: creativesQueryKeys.allCreativesWithHypothesis })
    .map((query) => query.state.data);

  return collectCachedCreatives(allCreativesData, allCreativesWithHypothesisData);
};

export const DEFAULT_CREATIVE_LOCALE = 'EN';

// A creative without localizations is treated as an EN creative.
const getCreativeLocales = (creative: CreativeEntry): string[] => {
  const locales = (creative.locales ?? []).filter((locale) => locale?.trim()).map(normalizeLocale);

  return locales.length ? locales : [DEFAULT_CREATIVE_LOCALE];
};

const resolveCreatives = (
  creativeIds: readonly (string | undefined)[],
  creativesById: Map<string, CreativeEntry>,
): CreativeEntry[] =>
  creativeIds.flatMap((creativeId) => {
    const creative = creativeId ? creativesById.get(String(creativeId)) : undefined;
    return creative ? [creative] : [];
  });

export const collectRequiredLocales = (
  creativeIds: readonly (string | undefined)[],
  creativesById: Map<string, CreativeEntry>,
): Set<string> =>
  new Set(resolveCreatives(creativeIds, creativesById).flatMap(getCreativeLocales));

export const getMissingTextLocales = (
  text: AdSettingTextEntry,
  requiredLocales: Set<string>,
): string[] => {
  const availableTextLocales = getTextAvailableLocales(text);

  return [...requiredLocales].filter((locale) => !availableTextLocales.has(locale));
};

export const getMissingLocalizationMessage = (missingLocales: string[]): string => {
  const localesLabel = missingLocales.join(', ');
  const creativeWord = missingLocales.length > 1 ? 'creatives' : 'creative';

  return `No localized text for ${localesLabel} ${creativeWord}. Add localized text or remove creative`;
};

export const getPackLocalizationIssue = (
  text: AdSettingTextEntry,
  pack: Pick<PackEntry, 'creatives' | 'adType'>,
  creativesById: Map<string, CreativeEntry>,
): string | null => {
  const creativeIds = pack.creatives ?? [];

  if (isDloAdType(pack.adType)) {
    const requiredLocales = collectRequiredLocales(creativeIds, creativesById);
    if (requiredLocales.size === 0) {
      return null;
    }

    const missingLocales = getMissingTextLocales(text, requiredLocales);

    return missingLocales.length ? getMissingLocalizationMessage(missingLocales) : null;
  }

  const textLocales = getTextAvailableLocales(text);
  if (textLocales.size === 0) {
    return null;
  }

  const unmatchedLocales = new Set(
    resolveCreatives(creativeIds, creativesById)
      .map(getCreativeLocales)
      .filter((creativeLocales) => !creativeLocales.some((locale) => textLocales.has(locale)))
      .flat(),
  );

  return unmatchedLocales.size ? getMissingLocalizationMessage([...unmatchedLocales]) : null;
};
